import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoIcon from './LogoIcon';
import PersonIcon from '@material-ui/icons/Person';
import { PersonRemoveIcon } from './PersonRemoveIcon';
import { AppRegistrationIcon } from './AppRegistrationIcon';
import { SignalIcon } from './SignalIcon';
import MemoryIcon from '@material-ui/icons/Memory';
import KitchenIcon from '@material-ui/icons/Kitchen';
import List from '@material-ui/icons/List';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import LocalCafeIcon from '@material-ui/icons/LocalCafe';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import EcoIcon from '@material-ui/icons/Eco';
import { NavLink } from 'react-router-dom';
import MenuIcon from '@material-ui/icons/Menu';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/Group';
import AssessmentIcon from '@mui/icons-material/Assessment';

import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import {
  AZURE_AD_GROUP_DPO_DEVELOPERS,
  AZURE_AD_GROUP_IOT_DEV,
  AZURE_AD_GROUP_IOT_PRODUCTION,
  AZURE_AD_GROUP_IOT_PRODUCTION_AMER,
  AZURE_AD_GROUP_IOT_PRODUCTION_APMEA,
  AZURE_AD_GROUP_IOT_PRODUCTION_EU,
  AZURE_AD_GROUP_DPO_IOT_APPLIANCE_LOG,
  AZURE_AD_GROUP_IOT_STAGING,
  getPhoenixUser,
  hasOwnership,
} from '@internal/plugin-phoenix-common';

import { useApp } from '@backstage/core-plugin-api';
import { SecDashIcon } from './SecDashIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 15,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
      >
        {isOpen ? <h1>&lt;Phoenix/&gt;</h1> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { identity } = getPhoenixUser();
  const app = useApp();

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarDivider />
        {hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) && (
          <SidebarItem icon={LocalCafeIcon} text="IDP">
            <SidebarSubmenu title="Catalog">
              <SidebarSubmenuItem
                title="Projects / Systems"
                to="catalog/idp#project"
                icon={app.getSystemIcon('kind:system')}
              />
              <SidebarSubmenuItem
                title="Services"
                to="catalog/idp#service"
                icon={app.getSystemIcon('kind:component')}
              />
              <SidebarSubmenuItem
                title="APIs"
                to="catalog?filters[kind]=api"
                subtitle="(coming soon!)"
                icon={app.getSystemIcon('kind:api')}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Infrastructure"
                to="catalog/idp"
                icon={app.getSystemIcon('kind:resource')}
              />
              <SidebarDivider />
              <SidebarSubmenuItem
                title="Groups"
                to="catalog?filters[kind]=group"
                icon={app.getSystemIcon('kind:group')}
              />
              <SidebarSubmenuItem
                title="Users"
                to="catalog?filters[kind]=user"
                icon={app.getSystemIcon('kind:user')}
              />
              <SidebarSubmenuItem
                title="Templates"
                to="catalog?filters[kind]=template"
                icon={app.getSystemIcon('scaffolder')}
              />
              <SidebarSubmenuItem
                title="Tasks"
                to="catalog/tasks"
                icon={FormatListBulletedIcon}
              />
              <SidebarSubmenuItem
                icon={HelpOutlineIcon}
                title="Docs"
                to="https://dpo-electrolux.atlassian.net/wiki/spaces/GI/pages/1163034651/Developer+Platform+IDP"
              />
            </SidebarSubmenu>
          </SidebarItem>
        )}
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {(hasOwnership(identity, AZURE_AD_GROUP_IOT_DEV) ||
            hasOwnership(identity, AZURE_AD_GROUP_IOT_STAGING) ||
            hasOwnership(identity, AZURE_AD_GROUP_IOT_PRODUCTION) ||
            hasOwnership(identity, AZURE_AD_GROUP_IOT_PRODUCTION_EU) ||
            hasOwnership(identity, AZURE_AD_GROUP_IOT_PRODUCTION_APMEA) ||
            hasOwnership(identity, AZURE_AD_GROUP_IOT_PRODUCTION_AMER) ||
            hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) ||
            hasOwnership(identity, AZURE_AD_GROUP_DPO_IOT_APPLIANCE_LOG)) && (
            <SidebarItem icon={MemoryIcon} text="IoT">
              <SidebarSubmenu title="IoT Platform">
                <SidebarSubmenuItem
                  icon={PersonIcon}
                  title="Users"
                  to="user-page"
                />
                <SidebarSubmenuItem
                  icon={KitchenIcon}
                  title="Appliances"
                  to="ocp-appliance-page"
                />
                <SidebarDivider />
                <SidebarSubmenuItem
                  icon={AppRegistrationIcon}
                  title="Appl. Registration"
                  subtitle="ECP only"
                  to="ocp-appliance-registration"
                />
                <SidebarSubmenuItem
                  icon={AppRegistrationIcon}
                  title="Appliance Debugger"
                  subtitle="Delta only"
                  to="appliance-debugger"
                />
                <SidebarSubmenuItem
                  icon={EcoIcon}
                  title="DAM Capability List"
                  to="dam-capability"
                />
                <SidebarDivider />
                <SidebarSubmenuItem
                  icon={AssessmentIcon}
                  title="Test Report"
                  to="test-report"
                />
                <SidebarSubmenuItem
                  icon={app.getSystemIcon('kind:api')}
                  title="Api Definition Exporter"
                  to="api-exporter"
                />
                {(hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) ||
                  hasOwnership(
                    identity,
                    'group:default/dpo-plugin-cf_read',
                  )) && (
                  <SidebarSubmenuItem
                    icon={AssignmentIcon}
                    title="Config Files"
                    to="config-files"
                  />
                )}
                {(hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) ||
                  hasOwnership(
                    identity,
                    'group:default/dpo-plugin-cf_read',
                  )) && (
                  <SidebarSubmenuItem
                    icon={AssignmentIcon}
                    title="HACL Msg. Conversion"
                    to="hacl-mapper-message-conversion"
                  />
                )}
                {(hasOwnership(
                  identity,
                  AZURE_AD_GROUP_DPO_IOT_APPLIANCE_LOG,
                ) ||
                  hasOwnership(
                    identity,
                    'group:default/dpo-plugin-iot-appliance-log-read',
                  )) && (
                  <SidebarSubmenuItem
                    icon={List}
                    title="File Store"
                    to="file-store-plugin"
                  />
                )}
              </SidebarSubmenu>
            </SidebarItem>
          )}
          <SidebarItem icon={AccountTreeOutlinedIcon} text="Org">
            <SidebarSubmenu title="Org">
              <SidebarSubmenuItem
                icon={AccountTreeOutlinedIcon}
                title="Org Chart"
                to="org-chart"
              />
              <SidebarSubmenuItem
                icon={GroupIcon}
                title="Team Management"
                to="org-teams"
              />
            </SidebarSubmenu>
          </SidebarItem>
          <SidebarItem icon={KitchenIcon} text="LabMate" to="labmate" />
          {hasOwnership(identity, 'group:default/dpo-plugin-ccpa_user') && (
            <SidebarItem icon={PersonRemoveIcon} to="ccpa" text="CCPA" />
          )}
          {hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) && (
            <SidebarItem
              icon={FormatListBulletedIcon}
              to="catalog-page"
              text="Service Catalog"
            />
          )}
          <SidebarItem icon={SignalIcon} text="Signal" to="signal" />
          {hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) && (
            <SidebarItem icon={SecDashIcon} text="SecDash" to="secdash" />
          )}
          <SidebarSpace />
          <SidebarDivider />
          {/* </div>
          }
        {/* Global nav, not org-specific */}
          {hasOwnership(identity, AZURE_AD_GROUP_DPO_DEVELOPERS) && (
            <SidebarItem
              icon={CreateComponentIcon}
              to="/catalog/idp/create"
              text="Create..."
            />
          )}
          {/* End global nav */}
          <SidebarDivider />
          <MyGroupsSidebarItem
            singularTitle="My Teams"
            pluralTitle="My Teams"
            icon={app.getSystemIcon('kind:group')!}
            filter={{ 'spec.type': 'team' }}
          />
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
          <SidebarItem
            icon={EcoIcon}
            to="$"
            text={
              document.querySelector('#app-version')?.getAttribute('value') ||
              ''
            }
          />
        </SidebarGroup>
      </Sidebar>
      {/* : <Progress />} */}
      {children}
    </SidebarPage>
  );
};
